import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { getProduct } from '../services/productsService'
import { addToHistory } from '../services/scanHistory'
import { getDisabledRetailers } from '../services/retailerPrefs'
import { getCity, setCity, COMMON_CITIES } from '../services/cityPref'
import { getSavedLocation, saveLocation, requestLocation } from '../services/geo'
import { prettyChain } from '../lib/chains'
import { cleanProductName } from '../lib/text'

// "4,99€" — comma decimal, no space, matching the Croatian retail convention.
function formatEur(num) {
  // Non-breaking space before € so the amount and symbol never wrap apart.
  return Number.isNaN(num) ? '—' : `${num.toFixed(2).replace('.', ',')} €`
}

// "2026-05-29" -> "29.05.2026."
function formatDate(value) {
  if (!value) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return m ? `${m[3]}.${m[2]}.${m[1]}.` : value
}

// Is the price date today (local time)? Today's prices don't need a date label.
function isToday(value) {
  if (typeof value !== 'string') return false
  const d = new Date()
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
  return value.slice(0, 10) === iso
}

const minPrice = (chain) => {
  const n = Number.parseFloat(chain.min_price)
  return Number.isNaN(n) ? Infinity : n
}

// Shared price-age sub-label (reused by the winner card and the rest list).
// Staleness is decided by the backend: a price is stale only
// when its chain hasn't been successfully crawled recently — NOT when the price
// itself simply hasn't changed. The FE only formats: it renders the BE `stale`
// flag as a warning, or — for a fresh price not set today — the "valid from" date.
function PriceAgeLabel({ stale, priceDate }) {
  const { t } = useTranslation()
  if (stale) {
    return <p className="text-xs text-warn-fg font-medium">{t('results.ageStale')}</p>
  }
  if (priceDate && !isToday(priceDate)) {
    return <p className="text-xs text-faint">{t('results.ageValidFrom', { date: formatDate(priceDate) })}</p>
  }
  return null
}

// "Akcija" pill — shown when the cheapest store's price is below its anchor
// (backend `on_promo`). Soft-green to read as a positive discount, distinct from
// the solid-green "cheapest" pill.
function PromoBadge({ onPromo }) {
  const { t } = useTranslation()
  if (!onPromo) return null
  return (
    <span className="bg-success-surface text-success-fg text-xs font-semibold px-2 py-0.5 rounded-pill">
      {t('results.promo')}
    </span>
  )
}

// Per-store signals the flat model discarded: which store is cheapest, and how
// many of the chain's stores carry the item. Muted sub-label; renders nothing
// when neither is known.
function StoreMeta({ chain }) {
  const { t } = useTranslation()
  const bits = []
  if (chain.cheapest_store?.city) bits.push(t('results.cheapestStore', { city: chain.cheapest_store.city }))
  if (chain.store_count > 1) bits.push(t('results.inStores', { count: chain.store_count }))
  if (!bits.length) return null
  return <p className="text-xs text-faint">{bits.join(' · ')}</p>
}

// Inner view is keyed by `ean` (see ResultsPage) so it remounts — and resets to
// the "loading" state — whenever a different barcode is looked up.
function ResultsView({ ean }) {
  const { t } = useTranslation()
  const [status, setStatus] = useState('loading') // loading | notFound | error | loaded
  const [product, setProduct] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  const [loadingTimeout, setLoadingTimeout] = useState(false)
  const [shareToast, setShareToast] = useState(false)
  const [city, setCityState] = useState(getCity())
  const [location, setLocation] = useState(getSavedLocation()) // { lat, lon } | null (geolocation)
  const [geoError, setGeoError] = useState(false)
  // True when a located scope (geo or city) had no prices, so we fell back to national.
  const [locatedFellBack, setLocatedFellBack] = useState(false)

  useEffect(() => {
    let active = true
    setStatus('loading')
    setLocatedFellBack(false)

    // Location precedence: precise geolocation → city → national.
    const scope = location
      ? { lat: location.lat, lon: location.lon }
      : city
        ? { city }
        : {}
    const located = Boolean(location || city)

    async function load() {
      try {
        let data
        try {
          data = await getProduct(ean, scope)
        } catch (err) {
          // A located lookup found nothing → fall back to national prices
          // (honest degradation: better than "not found" for a product that
          // exists, just not near the user / in that city).
          if (located && err.response?.status === 404) {
            data = await getProduct(ean, {})
            if (active) setLocatedFellBack(true)
          } else {
            throw err
          }
        }
        if (!active) return
        setProduct(data)
        setStatus('loaded')
        // Record only successful lookups — never 404s / errors.
        const chains = (data?.chains || []).filter((c) => !getDisabledRetailers().has(c.chain))
        const winnerPrice = chains.length ? Math.min(...chains.map(minPrice)) : 0
        addToHistory(data, winnerPrice)
      } catch (err) {
        if (!active) return
        setStatus(err.response?.status === 404 ? 'notFound' : 'error')
      }
    }
    load()

    return () => {
      active = false
    }
  }, [ean, retryCount, city, location])

  // Commit a new city (persist + trigger a re-fetch). No-op if unchanged.
  function handleCityCommit(value) {
    setCityState(setCity(value))
  }

  // Ask the browser for the user's location (primary "near you" path).
  async function handleUseLocation() {
    setGeoError(false)
    try {
      const loc = await requestLocation()
      saveLocation(loc)
      setLocation(loc) // triggers a radius-scoped re-fetch
    } catch {
      setGeoError(true) // declined / unavailable → the city picker remains
    }
  }

  // Drop the geolocation scope (back to city / national).
  function handleClearLocation() {
    saveLocation(null)
    setLocation(null)
  }

  // 8-second API timeout
  useEffect(() => {
    if (status !== 'loading') return
    const timer = setTimeout(() => setLoadingTimeout(true), 8000)
    return () => clearTimeout(timer)
  }, [status])

  // Hide retailers the user disabled in Settings (preference is local-storage
  // only). Then sort by the displayed metric (lowest price) so the cheapest is
  // on top and every "skuplje" delta is non-negative.
  const disabled = getDisabledRetailers()
  const allChains = product?.chains || []
  const chains = allChains
    .filter((c) => !disabled.has(c.chain))
    .sort((a, b) => minPrice(a) - minPrice(b))
  // All available retailers were hidden by the user's settings.
  const allHidden = allChains.length > 0 && chains.length === 0

  // Source-quality-demoted chains (e.g. Lidl): shown separately, de-emphasized,
  // never crowned. The backend already keeps these out of `chains`.
  const unverified = (product?.unverified_prices || [])
    .filter((c) => !disabled.has(c.chain))
    .sort((a, b) => minPrice(a) - minPrice(b))

  // Winner card data — all chains at the cheapest price, with savings vs next cheapest.
  const winnerPrice = chains.length ? minPrice(chains[0]) : 0
  const winnerChains = chains.filter((c) => Math.round((minPrice(c) - winnerPrice) * 100) === 0)
  const restChains = chains.filter((c) => Math.round((minPrice(c) - winnerPrice) * 100) !== 0)
  // Only one store carries this product — "cheapest" is technically true but
  // unhelpful, so label it plainly.
  const singleStore = chains.length === 1

  // Headline saving: cheapest vs the most expensive option shown.
  const maxRestPrice = restChains.length ? minPrice(restChains[restChains.length - 1]) : winnerPrice
  const maxSaving = maxRestPrice - winnerPrice

  // Share the comparison (URL already encodes the EAN). Native sheet first,
  // clipboard + toast as the fallback.
  async function handleShare() {
    if (!product) return
    const winner = winnerChains[0]
    const text = winner
      ? t('results.shareText', {
          name: cleanProductName(product.name),
          price: formatEur(winnerPrice),
          chain: prettyChain(winner.chain),
        })
      : cleanProductName(product.name)
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name, text, url })
      } catch {
        /* user dismissed the share sheet */
      }
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 2000)
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <main id="main-content" className="max-w-2xl mx-auto px-4 py-8">
      <Link to="/" className="min-h-[48px] inline-flex items-center px-2 mb-6 text-primary-text hover:underline">
        ← {t('results.rescan')}
      </Link>

      {status === 'loading' && !loadingTimeout && (
        <div className="space-y-4 animate-pulse" aria-label={t('results.loadingAria')}>
          <p className="text-base text-muted">{t('results.searchingPrices')}</p>
          {/* Winner card skeleton */}
          <div className="rounded-card bg-surface-2 p-4">
            <div className="h-4 bg-surface-2-hover rounded w-24 mb-3" />
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded bg-surface-2-hover" />
              <div className="flex-1 h-5 bg-surface-2-hover rounded w-32" />
              <div className="h-8 w-20 bg-surface-2-hover rounded" />
            </div>
          </div>
          {/* Result row skeletons */}
          <div className="h-[60px] bg-surface-2 rounded-card" />
          <div className="h-[60px] bg-surface-2 rounded-card" />
          <div className="h-[60px] bg-surface-2 rounded-card" />
        </div>
      )}

      {status === 'loading' && loadingTimeout && (
        <div role="alert" className="bg-surface rounded-card shadow-card p-6">
          <h1 className="text-xl font-semibold text-fg">{t('results.slowTitle')}</h1>
          <p className="mt-2 text-muted">{t('results.slowBody')}</p>
          <button onClick={() => window.location.reload()} className="mt-4 w-full bg-primary text-on-primary rounded-btn px-4 py-3.5 text-base font-semibold hover:bg-primary-hover">
            {t('results.retry')}
          </button>
        </div>
      )}

      {status === 'notFound' && (
        <div role="alert" className="bg-surface rounded-card shadow-card p-6">
          <h1 className="text-xl font-semibold text-fg">{t('results.notFoundTitle')}</h1>
          <p className="mt-2 text-fg">
            {t('results.notFoundBody')}
          </p>
          <p className="mt-2 text-muted">
            {t('results.notFoundHint')}
          </p>
          <p className="mt-4 text-sm text-muted">
            {t('results.scannedBarcode')} <span className="text-fg font-medium">{ean}</span>
          </p>
          <Link to="/" className="mt-4 block w-full text-center bg-primary text-on-primary rounded-btn px-4 py-3.5 text-base font-semibold hover:bg-primary-hover">
            {t('results.scanAnother')}
          </Link>
        </div>
      )}

      {status === 'error' && (
        <div role="alert" className="bg-surface rounded-card shadow-card p-6">
          <h1 className="text-xl font-semibold text-fg">{t('results.errorTitle')}</h1>
          <p className="mt-2 text-muted">
            {t('results.errorBody')}
          </p>
          <button
            onClick={() => { setStatus('loading'); setRetryCount(c => c + 1); setLoadingTimeout(false) }}
            className="mt-4 w-full bg-primary text-on-primary rounded-btn px-4 py-3.5 text-base font-semibold hover:bg-primary-hover"
          >
            {t('results.retry')}
          </button>
        </div>
      )}

      {status === 'loaded' && product && (
        <>
          <header className="mb-6 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-fg">{cleanProductName(product.name)}</h1>
              <p className="mt-1 text-muted">
                {[product.brand, product.quantity]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleShare}
              aria-label={t('results.shareAria')}
              className="shrink-0 mt-1 h-11 w-11 flex items-center justify-center rounded-full text-primary hover:bg-surface-2 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </header>

          {/* Location control — "cheapest near you". Primary: browser geolocation
              (radius search). Fallback: a free-text city picker (backend matches
              city diacritic-insensitively). */}
          <div className="mb-4" data-testid="location-bar">
            {location ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-fg font-medium">📍 {t('results.nearYou')}</span>
                <button
                  type="button"
                  data-testid="clear-location"
                  onClick={handleClearLocation}
                  className="text-primary hover:underline"
                >
                  {t('results.clearLocation')}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <button
                  type="button"
                  data-testid="use-location"
                  onClick={handleUseLocation}
                  className="rounded-btn bg-surface-2 px-3 py-1 font-medium text-fg hover:bg-surface-2-hover"
                >
                  📍 {t('results.useLocation')}
                </button>
                <label htmlFor="city-input" className="text-muted shrink-0">{t('results.cityLabel')}:</label>
                <input
                  id="city-input"
                  data-testid="city-input"
                  list="city-options"
                  defaultValue={city}
                  placeholder={t('results.cityPlaceholder')}
                  onBlur={(e) => handleCityCommit(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                  className="min-w-0 flex-1 rounded-btn border border-line bg-surface px-2 py-1 text-fg"
                />
                <datalist id="city-options">
                  {COMMON_CITIES.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
            )}
          </div>

          {geoError && (
            <p data-testid="geo-error" className="mb-3 text-sm text-warn-fg">{t('results.geoError')}</p>
          )}

          {locatedFellBack && (
            <p data-testid="location-fellback" className="mb-3 text-sm text-warn-fg">
              {location ? t('results.nearYouFellBack') : t('results.cityFellBack', { city })}
            </p>
          )}

          {shareToast && (
            <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-fg text-surface text-sm rounded-pill px-4 py-2 shadow-modal">
              {t('results.copied')}
            </div>
          )}

          {winnerChains.length > 0 && (
            <div
              data-testid="winner-card"
              className="bg-success-surface border-2 border-success rounded-card p-4 mb-4"
            >
              {winnerChains.length === 1 ? (
                <>
                  <div className="bg-success text-on-success text-sm font-semibold px-2 py-0.5 rounded-pill inline-block mb-3">
                    {t(singleStore ? 'results.onlyPrice' : 'results.cheapest')}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-surface flex items-center justify-center">
                      <img
                        src={`/retailers/${winnerChains[0].chain}.jpg`}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-bold text-fg">{prettyChain(winnerChains[0].chain)}</p>
                      <PriceAgeLabel stale={winnerChains[0].stale} priceDate={winnerChains[0].price_changed_at} />
                      <StoreMeta chain={winnerChains[0]} />
                    </div>
                    <div className="shrink-0 text-right">
                      {winnerChains[0].on_promo && (
                        <div className="mb-1 flex justify-end">
                          <PromoBadge onPromo />
                        </div>
                      )}
                      <p data-testid="winner-price" className="text-3xl font-bold text-fg">{formatEur(winnerPrice)}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-success text-on-success text-sm font-semibold px-2 py-0.5 rounded-pill inline-block">
                      {t('results.cheapest')}
                    </span>
                    <span data-testid="winner-price" className="text-3xl font-bold text-fg">{formatEur(winnerPrice)}</span>
                  </div>
                  <p className="text-sm text-muted mb-2">
                    {t('results.storesSamePrice', { count: winnerChains.length })}
                  </p>
                  <ul className="flex flex-wrap gap-2">
                    {winnerChains.map((chain) => (
                      <li
                        key={chain.chain}
                        className="flex items-center gap-2 bg-surface-2 rounded-pill py-1 pl-1 pr-3"
                      >
                        <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-surface flex items-center justify-center">
                          <img
                            src={`/retailers/${chain.chain}.jpg`}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-fg">{prettyChain(chain.chain)}</span>
                      </li>
                    ))}
                  </ul>
                  {winnerChains.some((c) => c.stale) && (
                    <p className="text-xs text-warn-fg font-medium mt-2">{t('results.someStale')}</p>
                  )}
                </>
              )}
            </div>
          )}

          {restChains.length > 0 && maxSaving >= 0.01 && (
            <p className="text-sm text-muted mb-3">
              <Trans
                i18nKey="results.saveUpTo"
                values={{ amount: formatEur(maxSaving) }}
                components={{ b: <span className="font-semibold text-savings-fg" /> }}
              />
            </p>
          )}

          {restChains.length ? (
            <ul className="space-y-2">
              {restChains.map((chain) => {
                const price = minPrice(chain)
                const delta = price - winnerPrice
                const pct = Math.round((delta / winnerPrice) * 100)
                const deltaText = pct === 0
                  ? t('results.moreExpensive', { amount: formatEur(delta) })
                  : t('results.moreExpensivePct', { amount: formatEur(delta), pct })
                return (
                  <li
                    key={`${chain.chain}-${chain.code}`}
                    className="flex items-center rounded-card border border-line bg-surface p-4"
                  >
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-surface flex items-center justify-center">
                      <img
                        src={`/retailers/${chain.chain}.jpg`}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="font-semibold text-fg">{prettyChain(chain.chain)}</p>
                      <PriceAgeLabel stale={chain.stale} priceDate={chain.price_changed_at} />
                      <StoreMeta chain={chain} />
                    </div>
                    <div className="ml-3 shrink-0 text-right">
                      {chain.on_promo && <div className="mb-1 flex justify-end"><PromoBadge onPromo /></div>}
                      <p data-testid="list-price" className="text-xl font-bold text-fg">{formatEur(price)}</p>
                      <p className="text-base text-muted">{deltaText}</p>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : allHidden ? (
            <div className="bg-surface rounded-card shadow-card p-6 text-center">
              <p className="text-muted">{t('results.allHidden')}</p>
              <Link to="/settings" className="mt-4 inline-block bg-primary text-on-primary rounded-btn px-6 py-3.5 text-base font-semibold hover:bg-primary-hover">
                {t('results.enableAll')}
              </Link>
            </div>
          ) : winnerChains.length === 0 && unverified.length === 0 ? (
            <p className="text-muted">{t('results.noPrices')}</p>
          ) : winnerChains.length === 1 ? (
            <p className="text-sm text-muted mt-2">{t('results.onlyOneStore')}</p>
          ) : null}

          {unverified.length > 0 && (
            <details data-testid="unverified" className="mt-4 rounded-card border border-warn-line bg-warn-surface p-4">
              <summary className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-fg">
                {t('results.unverifiedTitle')}
                <span className="bg-warn text-on-warn text-xs font-semibold px-1.5 rounded-pill">{unverified.length}</span>
              </summary>
              <p className="mt-2 text-xs text-warn-fg">{t('results.unverifiedNote')}</p>
              <ul className="mt-3 space-y-2">
                {unverified.map((chain) => (
                  <li key={chain.chain} className="flex items-center rounded-card bg-surface p-3">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-surface flex items-center justify-center">
                      <img
                        src={`/retailers/${chain.chain}.jpg`}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="font-semibold text-fg">{prettyChain(chain.chain)}</p>
                      <PriceAgeLabel stale={chain.stale} priceDate={chain.price_changed_at} />
                      <StoreMeta chain={chain} />
                    </div>
                    <div className="ml-3 shrink-0 text-right">
                      {chain.on_promo && <div className="mb-1 flex justify-end"><PromoBadge onPromo /></div>}
                      <p className="text-lg font-bold text-muted">{formatEur(minPrice(chain))}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <p className="mt-6 text-center text-xs text-faint whitespace-pre-line">{t('results.lowestReported')}</p>

          <p className="mt-2 text-center text-xs text-faint">
            EAN: <span className="font-mono select-all">{product.ean}</span>
          </p>
        </>
      )}
    </main>
  )
}

export default function ResultsPage() {
  const { ean } = useParams()
  return <ResultsView key={ean} ean={ean} />
}
